import { Injectable, inject } from '@angular/core';
import { MatomoTracker } from '@ngx-matomo/tracker';
import { Logger } from 'shared';
import {
  PageViewData,
  EventData,
  EcommerceItem,
  EcommerceOrder,
  SiteSearchData,
  LinkTrackingData,
  GoalData,
  ContentData
} from '../models/analytics.models';
import { MatomoService } from './matomo.service';

/**
 * MatomoTrackerService
 *
 * Service for tracking user interactions, events, and e-commerce activities.
 * All tracking methods automatically check if Matomo is enabled before tracking.
 */
@Injectable({
  providedIn: 'root'
})
export class MatomoTrackerService {
  protected logger = new Logger('MatomoTrackerService');
  private readonly tracker = inject(MatomoTracker);
  private readonly matomoService = inject(MatomoService);

  /**
   * Track a page view
   * @param data Optional page view data
   */
  trackPageView(data?: PageViewData): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      if (data?.customTitle) {
        this.tracker.setDocumentTitle(data.customTitle);
      }
      if (data?.customUrl) {
        this.tracker.setCustomUrl(data.customUrl);
      }

      this.tracker.trackPageView(data?.documentTitle);
      this.logger.log('Matomo page view tracked:', data?.customUrl || window.location.pathname);
    } catch (error) {
      this.logger.error('Failed to track page view:', error);
    }
  }

  /**
   * Track a custom event
   * @param data Event data (category, action, name, value)
   */
  trackEvent(data: EventData): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.trackEvent(
        data.category,
        data.action,
        data.name,
        data.value
      );
      this.logger.log('Matomo event tracked:', data);
    } catch (error) {
      this.logger.error('Failed to track event:', error);
    }
  }

  /**
   * Track site search
   * @param data Site search data
   */
  trackSiteSearch(data: SiteSearchData): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.trackSiteSearch(
        data.keyword,
        data.category,
        data.resultsCount
      );
      this.logger.log('Matomo site search tracked:', data);
    } catch (error) {
      this.logger.error('Failed to track site search:', error);
    }
  }

  /**
   * Track a goal conversion
   * @param data Goal data
   */
  trackGoal(data: GoalData): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.trackGoal(data.idGoal, data.customRevenue);
      this.logger.log('Matomo goal tracked:', data);
    } catch (error) {
      this.logger.error('Failed to track goal:', error);
    }
  }

  /**
   * Track a link (outbound link or download)
   * @param data Link tracking data
   */
  trackLink(data: LinkTrackingData): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.trackLink(data.url, data.linkType);
      this.logger.log('Matomo link tracked:', data);
    } catch (error) {
      this.logger.error('Failed to track link:', error);
    }
  }

  /**
   * Track a download
   * @param url Download URL
   */
  trackDownload(url: string): void {
    this.trackLink({ url, linkType: 'download' });
  }

  /**
   * Track content impression
   * @param data Content data
   */
  trackContentImpression(data: ContentData): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.trackContentImpression(
        data.contentName,
        data.contentPiece || '',
        data.contentTarget || ''
      );
      this.logger.log('Matomo content impression tracked:', data);
    } catch (error) {
      this.logger.error('Failed to track content impression:', error);
    }
  }

  /**
   * Track content interaction
   * @param interaction Interaction type (e.g., 'click')
   * @param data Content data
   */
  trackContentInteraction(interaction: string, data: ContentData): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.trackContentInteraction(
        interaction,
        data.contentName,
        data.contentPiece || '',
        data.contentTarget || ''
      );
      this.logger.log('Matomo content interaction tracked:', { interaction, ...data });
    } catch (error) {
      this.logger.error('Failed to track content interaction:', error);
    }
  }

  // ============================================
  // E-COMMERCE TRACKING
  // ============================================

  /**
   * Add e-commerce item to the order
   * @param item E-commerce item
   */
  addEcommerceItem(item: EcommerceItem): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.addEcommerceItem(
        item.sku,
        item.name,
        item.category,
        item.price,
        item.quantity
      );
      this.logger.log('Matomo e-commerce item added:', item);
    } catch (error) {
      this.logger.error('Failed to add e-commerce item:', error);
    }
  }

  /**
   * Remove e-commerce item from the order
   * @param sku Product SKU
   */
  removeEcommerceItem(sku: string): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.removeEcommerceItem(sku);
      this.logger.log('Matomo e-commerce item removed:', sku);
    } catch (error) {
      this.logger.error('Failed to remove e-commerce item:', error);
    }
  }

  /**
   * Clear all e-commerce items from the order
   */
  clearEcommerceCart(): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.clearEcommerceCart();
      this.logger.log('Matomo e-commerce cart cleared');
    } catch (error) {
      this.logger.error('Failed to clear e-commerce cart:', error);
    }
  }

  /**
   * Track e-commerce cart update
   * @param cartTotal Total cart value
   */
  trackEcommerceCartUpdate(cartTotal: number): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.trackEcommerceCartUpdate(cartTotal);
      this.logger.log('Matomo e-commerce cart update tracked:', cartTotal);
    } catch (error) {
      this.logger.error('Failed to track e-commerce cart update:', error);
    }
  }

  /**
   * Track e-commerce order
   * @param order Order data
   * @param items Order items
   */
  trackEcommerceOrder(order: EcommerceOrder, items: EcommerceItem[]): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      // Clear previous items
      this.clearEcommerceCart();

      // Add all items
      items.forEach(item => this.addEcommerceItem(item));

      // Track the order
      this.tracker.trackEcommerceOrder(
        order.orderId,
        order.grandTotal,
        order.subTotal,
        order.tax,
        order.shipping,
        order.discount
      );

      this.logger.log('Matomo e-commerce order tracked:', {
        order,
        itemCount: items.length
      });
    } catch (error) {
      this.logger.error('Failed to track e-commerce order:', error);
    }
  }

  /**
   * Set e-commerce view (product or category page)
   * @param sku Product SKU
   * @param name Product name
   * @param category Product category
   * @param price Product price
   */
  setEcommerceView(sku: string, name?: string, category?: string, price?: number): void {
    if (!this.matomoService.isEnabled()) return;

    try {
      this.tracker.setEcommerceView(sku, name, category, price);
      this.logger.log('Matomo e-commerce view set:', { sku, name, category, price });
    } catch (error) {
      this.logger.error('Failed to set e-commerce view:', error);
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Track multiple events at once
   * @param events Array of event data
   */
  trackEvents(events: EventData[]): void {
    events.forEach(event => this.trackEvent(event));
  }

  /**
   * Track form submission
   * @param formName Form identifier
   * @param success Whether submission was successful
   */
  trackFormSubmit(formName: string, success: boolean = true): void {
    this.trackEvent({
      category: 'Form',
      action: success ? 'Submit Success' : 'Submit Error',
      name: formName
    });
  }

  /**
   * Track button click
   * @param buttonName Button identifier
   * @param category Optional category (defaults to 'Button')
   */
  trackButtonClick(buttonName: string, category: string = 'Button'): void {
    this.trackEvent({
      category,
      action: 'Click',
      name: buttonName
    });
  }

  /**
   * Track navigation action
   * @param destination Where user navigated to
   */
  trackNavigation(destination: string): void {
    this.trackEvent({
      category: 'Navigation',
      action: 'Navigate',
      name: destination
    });
  }

  /**
   * Track error
   * @param errorType Type of error
   * @param errorMessage Error message
   */
  trackError(errorType: string, errorMessage: string): void {
    this.trackEvent({
      category: 'Error',
      action: errorType,
      name: errorMessage
    });
  }

  /**
   * Track user interaction with duration
   * @param category Interaction category
   * @param action Interaction action
   * @param name Interaction name
   * @param durationMs Duration in milliseconds
   */
  trackInteractionWithDuration(
    category: string,
    action: string,
    name: string,
    durationMs: number
  ): void {
    this.trackEvent({
      category,
      action,
      name,
      value: Math.round(durationMs / 1000) // Convert to seconds
    });
  }
}
